import React, { useEffect, useRef, useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { useRideGate } from '../hooks/useRideGate';
import { api } from '../services/api';
import { INTRO_RIDE, SPOR1_CAR } from '../worldRide';
import { camBasis } from '../services/worldObjects';
import { IS_GUEST } from '../config';

// RideOverlay — the DOM half of Spor1's ride (Build_Game_Intro §4.1-4.2, as
// re-staged 2026-08-02: Lyra's call says FIND him — main strip, yellow Lotus).
//
// Three small things, none of them a menu:
//
//   THE FIND — while the ride quest is open and you're not at the car yet, a
//   quiet chip keeps Lyra's errand alive: "Find Spor1 · main strip · yellow
//   Lotus." Walking up to the Lotus (Spor1Lotus, in-canvas) swaps it for the
//   pickup card — "Window down. It's Spor1." One button: get in. Walking away
//   costs nothing; the car stays parked (§5.2 — never a closed door). The
//   phone's Rides app can still hail him from anywhere as the fallback.
//
//   THE REVEAL — a landmark going past gets a lower-third card for a few
//   seconds. The tour is the map tutorial; this is the tutorial's ink.
//
//   THE CHIP — a quiet "with Spor1" marker while the car has the camera, so
//   the state is legible without chrome.

export default function RideOverlay() {
  const entered = useWorldStore((s) => s.entered);
  const interior = useWorldStore((s) => s.interior);
  const scene = useWorldStore((s) => s.scene);
  const ride = useWorldStore((s) => s.ride);
  const rideNote = useWorldStore((s) => s.rideNote);
  const myAvatarId = useWorldStore((s) => s.myAvatarId);

  const [boarding, setBoarding] = useState(false);
  const [reveal, setReveal] = useState(null);   // local copy — outlives the store note

  // The reveal card holds a beat after the store note is consumed by the scene.
  useEffect(() => {
    if (!rideNote) return undefined;
    setReveal(rideNote);
    const t = setTimeout(() => setReveal(null), 4200);
    return () => clearTimeout(t);
  }, [rideNote]);

  // One rule for both paths now (useRideGate): the errand is open only once
  // Lyra's call has been answered. Members previously showed the "Find Spor1"
  // chip on the strength of the ride quest alone — which a seeded returning
  // artist has at spawn, before she rings.
  const { rideOpen } = useRideGate();
  const questOpen = entered && !interior && !scene && !ride && !boarding && rideOpen;

  // How far to the Lotus — THE CITY'S OWN parked Elise (store.landmark, the
  // spot the fly-in has always landed beside), never a planted copy. camBasis
  // polled once a second, same discipline as WalkGuide: a DOM component must
  // not re-render per camera frame.
  const landmark = useWorldStore((s) => s.landmark);
  const carX = landmark ? landmark[0] : SPOR1_CAR.pos[0];
  const carZ = landmark ? landmark[2] : SPOR1_CAR.pos[1];
  const [nearCar, setNearCar] = useState(false);
  useEffect(() => {
    if (!questOpen) { setNearCar(false); return undefined; }
    const tick = () => {
      const d = Math.hypot(carX - camBasis.px, carZ - camBasis.pz);
      setNearCar(d <= SPOR1_CAR.reach);
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [questOpen, carX, carZ]);

  const showFind = questOpen && !nearCar;
  const showPickup = questOpen && nearCar;

  // THE OBJECTIVE ANNOUNCES ITSELF (Khari: the corner chip was "almost
  // invisible"). When the errand first exists it SPAWNS centre screen —
  // grow, fade, a small bounce — holds a beat, fades out, and only then
  // fades into its resting spot top-right. One ceremony per page load.
  const [announce, setAnnounce] = useState('idle');   // idle | center | out | rest
  const announcedRef = useRef(false);
  useEffect(() => {
    if (!showFind) return undefined;
    if (announcedRef.current) { setAnnounce('rest'); return undefined; }
    announcedRef.current = true;
    setAnnounce('center');
    // Centre hold ~4s (Khari: "let it linger a bit longer" — was 2.3s):
    // long enough to actually read twice, gone before it reads as stuck.
    const t1 = setTimeout(() => setAnnounce('out'), 4000);
    const t2 = setTimeout(() => setAnnounce('rest'), 4450);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [showFind]);

  async function getIn() {
    if ((!myAvatarId && !IS_GUEST) || boarding) return;
    setBoarding(true);
    // The server picks the variant (interview vs homecoming, §5.1) — canon,
    // not a client preference. A guest is by definition new: the interview,
    // no lookup. Unreachable server → the interview, safely.
    let key = 'act2_ride_spor1';
    let title = "Spor1's ride";
    if (!IS_GUEST) {
      try {
        const d = await api.dialogueRide(myAvatarId);
        if (d?.scene) { key = d.scene; title = d.title || title; }
      } catch { /* default stands */ }
    }
    const st = useWorldStore.getState();
    st.openScene({ key, title, quest: IS_GUEST ? null : 'come_up_the_ride', room: null });
    st.startRide({ sceneKey: key, pace: INTRO_RIDE.pace });
    setBoarding(false);
  }

  return (
    <>
      {showFind && (announce === 'center' || announce === 'out') && (
        <div style={{ ...findCenter, animation: announce === 'out' ? 'spor1Out 0.45s ease forwards' : 'spor1Spawn 0.55s cubic-bezier(0.34, 1.4, 0.64, 1) both' }}>
          <div style={{ font: '600 11px system-ui', letterSpacing: 1.6, opacity: 0.6, marginBottom: 4 }}>NEW ERRAND</div>
          🟡 Find Spor1 · main strip · the yellow Lotus
        </div>
      )}
      {showFind && announce === 'rest' && (
        <div style={{ ...findChip, animation: 'spor1In 0.5s ease both' }}>
          🟡 Find Spor1 · main strip · the yellow Lotus
        </div>
      )}
      <style>{`
        @keyframes spor1Spawn { 0% { opacity: 0; transform: translate(-50%, -50%) scale(0.55); } 70% { opacity: 1; transform: translate(-50%, -50%) scale(1.07); } 100% { opacity: 1; transform: translate(-50%, -50%) scale(1); } }
        @keyframes spor1Out { to { opacity: 0; transform: translate(-50%, -50%) scale(0.94); } }
        @keyframes spor1In { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: none; } }
      `}</style>

      {showPickup && (
        <div style={pickupWrap}>
          <div style={{ font: '600 12px system-ui', opacity: 0.7, letterSpacing: 0.3 }}>
            The yellow Lotus. Window down.
          </div>
          <div style={{ font: '800 16px system-ui', margin: '3px 0 10px' }}>It's Spor1.</div>
          <button style={getInBtn} onClick={getIn}>Get in</button>
        </div>
      )}

      {ride && !reveal && (
        <div style={chip}>🚗 with Spor1</div>
      )}

      {reveal && (
        <div style={{ ...revealWrap, borderColor: reveal.accent || 'rgba(255,178,94,0.6)' }}>
          <div style={{ font: '600 10px system-ui', opacity: 0.55, letterSpacing: 1.2 }}>NOW PASSING</div>
          <div style={{ font: '800 18px system-ui', color: reveal.accent || '#ffe9c4', marginTop: 2 }}>{reveal.name}</div>
          <div style={{ font: '600 11.5px system-ui', opacity: 0.7, marginTop: 3, maxWidth: 300, lineHeight: 1.4 }}>{reveal.line}</div>
        </div>
      )}
    </>
  );
}

const findChip = {
  position: 'fixed', top: 'max(14px, env(safe-area-inset-top))', right: 14, zIndex: 69,
  pointerEvents: 'none', padding: '8px 14px', borderRadius: 999,
  font: '700 12.5px system-ui', color: '#ffe9c4',
  border: '1px solid rgba(245,197,24,0.75)', background: 'rgba(20,12,8,0.92)',
  backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
  boxShadow: '0 6px 22px rgba(0,0,0,0.45)',
};
const findCenter = {
  position: 'fixed', left: '50%', top: '38%', transform: 'translate(-50%, -50%)',
  zIndex: 74, pointerEvents: 'none', padding: '16px 26px', borderRadius: 18,
  font: '800 17px system-ui', color: '#ffe9c4', textAlign: 'center',
  border: '1px solid rgba(245,197,24,0.8)', background: 'rgba(20,12,8,0.94)',
  backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
  boxShadow: '0 24px 70px rgba(0,0,0,0.6), 0 0 34px rgba(245,197,24,0.18)',
};
const pickupWrap = {
  position: 'fixed', left: '50%', transform: 'translateX(-50%)',
  bottom: 'max(96px, calc(env(safe-area-inset-bottom) + 96px))', zIndex: 71,
  padding: '14px 22px', borderRadius: 16, textAlign: 'center',
  border: '1px solid rgba(255,178,94,0.5)', background: 'rgba(20,12,8,0.92)',
  color: '#ffe9c4', pointerEvents: 'auto',
  backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
  boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
};
const getInBtn = {
  padding: '10px 26px', borderRadius: 999, border: 'none', cursor: 'pointer',
  font: '800 14px system-ui', background: 'linear-gradient(180deg,#ffb25e,#ff5e78)', color: '#1a0f00',
};
const chip = {
  position: 'fixed', top: 'max(14px, env(safe-area-inset-top))', left: '50%',
  transform: 'translateX(-50%)', zIndex: 70, pointerEvents: 'none',
  padding: '6px 14px', borderRadius: 999, font: '700 12px system-ui',
  border: '1px solid rgba(255,178,94,0.4)', background: 'rgba(20,12,8,0.75)',
  color: '#ffe9c4', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
};
const revealWrap = {
  position: 'fixed', left: '50%', transform: 'translateX(-50%)',
  top: 'max(18px, env(safe-area-inset-top))', zIndex: 70, pointerEvents: 'none',
  padding: '10px 20px', borderRadius: 14, textAlign: 'center',
  border: '1px solid', background: 'rgba(14,9,6,0.88)', color: '#ffe9c4',
  backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
};
